// Shared bordered-card wrapper for AccountWorkspace panels.
// Title row + optional count + body + optional footer.

import { Children } from 'react';

export default function Panel({ title, count, children, footer, empty }) {
  // THE EMPTY MESSAGE IS A CAPTION, NOT A REPLACEMENT. (David, 191.)
  //
  // This was `empty ? <p>{empty}</p> : children`, so every action control a
  // panel put in its children was unreachable in EXACTLY the state that needs
  // it. "No additional interests on this account. Add lienholders..." rendered
  // with the Add button swallowed, on the deal David was trying to add Dekalb
  // Peachtree to. The message told him what to do and the same expression
  // removed the way to do it.
  //
  // Fourth instance of the dead-control class this month, and the only one so
  // far that was in a SHARED wrapper: one ternary, fifteen call sites. That is
  // why the guard belongs here rather than on any one panel.
  //
  // Children are rendered unconditionally now. Every caller already gates its
  // own row block on a length check (or maps an empty array, which renders an
  // empty container), so nothing draws a stray header or a second message.
  const hasChildren = Children.toArray(children).length > 0;
  return (
    <section className="rounded-card border border-border bg-surface">
      <header className="flex items-baseline justify-between border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">
          {title}
        </h2>
        {typeof count === 'number' ? (
          <span className="font-mono text-[10px] text-text-dim">{count}</span>
        ) : null}
      </header>
      <div className="p-4">
        {empty ? (
          // The gap only exists when there is something under it: a panel whose
          // children genuinely render nothing must not grow a hanging margin.
          <p className={`text-xs italic text-text-dim${hasChildren ? ' mb-3' : ''}`}>{empty}</p>
        ) : null}
        {children}
      </div>
      {footer ? (
        <footer className="border-t border-border px-4 py-2 text-[11px]">{footer}</footer>
      ) : null}
    </section>
  );
}
